import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from "@angular/core";
import { FormControl, NonNullableFormBuilder, UntypedFormGroup } from "@angular/forms";
import { Subject, takeUntil } from "rxjs";
import { AdvancedRadiusSettings } from "./type/sp-advanced-radius-settings.type";
import { CSS_RESTRICTIONS } from '@common/constants';

@Component ({
    selector: "sp-advanced-radius-settings",
    templateUrl: "./sp-advanced-radius-settings.component.html",
    styleUrls: ["./sp-advanced-radius-settings.component.less"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})

export class SpAdvancedRadiusSettingsComponent implements OnInit {
    @Input() public radiusSettings: AdvancedRadiusSettings;
    @Output() public valueChangeEvent: EventEmitter<AdvancedRadiusSettings> = new EventEmitter<AdvancedRadiusSettings>();
    
    public form: UntypedFormGroup;
    private readonly unsubscribe$ = new Subject<void>();

    constructor(private readonly fb: NonNullableFormBuilder) {
        this.initForm();
    }
    
    public ngOnInit(): void {
        this.form.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((changes) => this.emitChanges(changes));

        this.setFormData();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        const { radiusSettings } = changes;

        if (radiusSettings?.currentValue && !radiusSettings.isFirstChange()) {
            const newSettings = radiusSettings.currentValue;
            const prevSettings = radiusSettings.previousValue;
    
            const updatedFields: Partial<typeof newSettings> = {};
    
            Object.keys(newSettings).forEach((key) => {
                if (newSettings[key] !== prevSettings?.[key]) {
                    updatedFields[key] = newSettings[key];
                }
            });
    
            if (Object.keys(updatedFields).length > 0) {
                this.form.patchValue(updatedFields, { emitEvent: false });
            }
        }
    }

    private initForm(): void {
        this.form = this.fb.group({
            borderTopLeftRadius: null,
            borderTopRightRadius: null,
            borderBottomLeftRadius: null,
            borderBottomRightRadius: null,
        });
    }

    private setFormData(): void {
        const base: AdvancedRadiusSettings = {
            borderTopLeftRadius: this.radiusSettings.borderTopLeftRadius,
            borderTopRightRadius: this.radiusSettings.borderTopRightRadius,
            borderBottomLeftRadius: this.radiusSettings.borderBottomLeftRadius,
            borderBottomRightRadius: this.radiusSettings.borderBottomRightRadius,
        };

        this.form.patchValue(base, { emitEvent: false });
    }

    private emitChanges(changes: AdvancedRadiusSettings): void {
        this.valueChangeEvent.emit(changes);
    }

    public get borderTopLeftRadius(): FormControl<number> {
        return this.form.get("borderTopLeftRadius") as FormControl<number>;
    }
    public get borderTopRightRadius(): FormControl<number> {
        return this.form.get("borderTopRightRadius") as FormControl<number>;
    }
    public get borderBottomLeftRadius(): FormControl<number> {
        return this.form.get("borderBottomLeftRadius") as FormControl<number>;
    }
    public get borderBottomRightRadius(): FormControl<number> {
        return this.form.get("borderBottomRightRadius") as FormControl<number>;
    }

    public get borderTopLeftRadiusValue(): number {
        return this.borderTopLeftRadius.value as number;
    }

    public get borderTopRightRadiusValue(): number {
        return this.borderTopRightRadius.value as number;
    }

    public get borderBottomLeftRadiusValue(): number {
        return this.borderBottomLeftRadius.value as number;
    }

    public get borderBottomRightRadiusValue(): number {
        return this.borderBottomRightRadius.value as number;
    }

    public get BORDER_RADIUS_MIN(): number {
        return CSS_RESTRICTIONS.BORDER_RADIUS_MIN;
    }

    public get BORDER_RADIUS_MAX(): number {
        return CSS_RESTRICTIONS.BORDER_RADIUS_MAX;
    }
    
    public setBorderTopLeftRadius(value: number): void {
        this.borderTopLeftRadius.setValue(value);
    }

    public setBorderTopRightRadius(value: number): void {
        this.borderTopRightRadius.setValue(value);
    }

    public setBorderBottomLeftRadius(value: number): void {
        this.borderBottomLeftRadius.setValue(value);
    }

    public setBorderBottomRightRadius(value: number): void {    
        this.borderBottomRightRadius.setValue(value);
    }
}